import React, { useEffect, useRef, useState } from "react";
import { FaPhone, FaEnvelope, FaCheckCircle } from "react-icons/fa";

function DenmarkVisa() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const contentRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 10);

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          setActiveIndex(index);
        }
      });
    }, observerOptions);

    sectionRefs.current.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (index) => {
    setActiveIndex(index);
    const section = sectionRefs.current[index];
    const contentElement = contentRef.current;

    const scrollPosition = section.offsetTop - contentElement.offsetTop;

    contentElement.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });
  };

  return (
    <>
      <div className="flex flex-wrap">
  <div className="flex flex-col items-center justify-center px-4 py-7 md:justify-between md:px-20 xl:flex-row"></div>

  <div className="mb-20 flex min-h-screen">
    <div
      ref={titleRef}
      className="sticky top-20 hidden h-screen w-1/4 overflow-y-auto bg-gray-100 p-4 md:block"
    >
      <ul className="space-y-2">
        <li
          onClick={() => scrollToSection(0)}
          className={`cursor-pointer p-4 ${
            activeIndex === 0
              ? "bg-blue-500 text-white"
              : "bg-white text-[#2C3F69]"
          } shadow rounded-md`}
        >
          Long-Term Residence Permit
        </li>
        <li
          onClick={() => scrollToSection(1)}
          className={`cursor-pointer p-4 ${
            activeIndex === 1
              ? "bg-blue-500 text-white"
              : "bg-white text-[#2C3F69]"
          } shadow rounded-md`}
        >
          Denmark Green Card Program
        </li>
        <li
          onClick={() => scrollToSection(2)}
          className={`cursor-pointer p-4 ${
            activeIndex === 2
              ? "bg-blue-500 text-white"
              : "bg-white text-[#2C3F69]"
          } shadow rounded-md`}
        >
          General Visa Requirements for Denmark
        </li>
        <li
          onClick={() => scrollToSection(3)}
          className={`cursor-pointer p-4 ${
            activeIndex === 3
              ? "bg-blue-500 text-white"
              : "bg-white text-[#2C3F69]"
          } shadow rounded-md`}
        >
          Denmark Visa Point System
        </li>
      </ul>
    </div>

    <div
      ref={contentRef}
      className="mb-10 h-screen overflow-y-auto p-4 md:w-3/4 lg:w-3/4 xl:w-3/4"
    >
      {/* Long-Term Residence Permit Section */}
      <div ref={(el) => (sectionRefs.current[0] = el)}>
        <p className="text-3xl font-bold text-[#011473]">Long-Term Residence Permit</p>
        <p className="mt-4 text-[#2C3F69]">
          To qualify for permanent residency, an individual must reside in Denmark for a minimum of 8 years. Specific professions, correct documentation and a clean criminal record can expedite the visa process. In certain cases, residency may be shortened to 4 years, especially if the applicant has strong ties to Denmark.
        </p>
        <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
          <li>Work Visa: Fast Track, Pay Limit, Positive List, etc.</li>
          <li>Visa for Accompanying Family Members</li>
          <li>Religious Workers</li>
          <li>Au Pair Program</li>
          <li>Greenland and Faroe Islands Visa</li>
          <li>Visa for Researchers</li>
          <li>Start-up Denmark Scheme</li>
          <li>Internship Visa</li>
          <li>Family Reunification Visa</li>
        </ul>
      </div>

      {/* Denmark Green Card Program Section */}
      <div ref={(el) => (sectionRefs.current[1] = el)} className="mt-10">
        <p className="text-3xl font-bold text-[#011473]">Denmark Green Card Program</p>
        <p className="mt-4 text-[#2C3F69]">
          The Danish Green Card Program is a points-based work permit program lasting 18 months, with the possibility of extension. No job offer is required, allowing applicants to live and work in Denmark.
        </p>
        <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
          <li>Initial grant for 18 months</li>
          <li>Family members (spouse and children under 18) are eligible to join</li>
          <li>Free travel across the Schengen Zone without needing extra visas</li>
          <li>Eligibility for permanent residency upon fulfilling specific requirements</li>
        </ul>
      </div>

      {/* General Denmark Visa Requirements Section */}
      <div ref={(el) => (sectionRefs.current[2] = el)} className="mt-10">
        <p className="text-3xl font-bold text-[#011473]">General Visa Requirements for Denmark</p>
        <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
          <li>Completed and signed application form</li>
          <li>Travel insurance with coverage of at least EUR 30,000</li>
          <li>Proof of hotel reservation</li>
          <li>Bank statements</li>
          <li>Valid passport</li>
          <li>Flight reservation</li>
          <li>Medical reports</li>
          <li>Enrollment letter for study visa applications</li>
        </ul>
      </div>

      {/* Denmark Visa Point System Section */}
      <div ref={(el) => (sectionRefs.current[3] = el)} className="mt-10">
        <p className="text-3xl font-bold text-[#011473]">Denmark Visa Point System</p>
        <ul className="mt-4 list-disc pl-6 text-[#2C3F69]">
          <li>Points for Education Level</li>
          <li>Points for Language Proficiency</li>
          <li>Work Experience Points</li>
          <li>Adaptability Points</li>
        </ul>
        <p className="mt-4 text-[#2C3F69]">
          Initially granted for 3 years, the visa can be extended for an additional 4 years, making a total of 7 years.
        </p>
        <p className="mt-4 text-[#2C3F69]">
          Family members (spouse, registered partner, or children under 18) living with you are eligible for a residence permit. To qualify as a partner, you must typically have lived together for a minimum of 18 to 24 months.
        </p>
      </div>
    </div>
  </div>
</div>

<div className="bg-gray-50 py-12">
  <div className="container mx-auto px-6 lg:px-20">
    <h2 className="mb-8 text-center text-3xl font-bold text-[#011473]">
      Advantages of Migrating to Denmark
    </h2>
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-2">
      <div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            Enjoy a balanced work-life environment with excellent job prospects
          </p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            Free education for up to two children under the age of 5
          </p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">Family reunification visa available</p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            Spouse eligible for full-time employment opportunities
          </p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            Access to Erasmus Mundus scholarships for sponsored education
          </p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">Earn in euros while living in Denmark</p>
        </div>
      </div>
      <div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            Benefit from world-class healthcare services
          </p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            One of the top global education systems
          </p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            International students can access a 6-month job search visa
          </p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            Work 20 hours per week and earn between €10–15 per hour
          </p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">Access high-quality education</p>
        </div>
        <div className="mb-4 flex items-start">
          <FaCheckCircle className="mt-1 h-5 w-5 flex-shrink-0 text-blue-500" />
          <p className="ml-3 text-gray-700">
            Recognized as one of the happiest and safest countries to live in
          </p>
        </div>
      </div>
    </div>
  </div>
</div>



    </>
  );
}

export default DenmarkVisa;
